import React, { useState, useEffect, useCallback } from "react";
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import AssessmentRounded from '@mui/icons-material/AssessmentRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import Header from "components/Header";
import {
  Box,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Autocomplete,
  Alert,
  Popper,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import checkAdminAuth from "helpers/AdminAuth";
import { StatusCards, SourceAnalysis, RecordStatus, TopSources, MonthlyTrend, QuickActions, Insights } from "components/analytics/AnalyticsPanels";
import { destinationLink, downloadCsv, sourceRows, number, summaryCount } from "components/analytics/data";
import useRecordStatus from 'components/analytics/useRecordStatus';
import OverviewChart from "components/OverviewChart";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import CasesOutlinedIcon from "@mui/icons-material/CasesOutlined";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import AIChat from "../../AIChat";
import CustomerListbox, { prepareCustomerNavigation } from "./CustomerListbox";
import PageLoader from "components/PageLoader";
import EntityRecords from './EntityRecords';

const API_BASE = process.env.REACT_APP_BASE_URL?.replace(/\/$/, "") || "";
const hasMonthStats = (data) => [
  "currentMonthCustomers", "currentMonthContacts", "currentMonthInvoices", "currentMonthInvoiceAmount",
].every((field) => typeof data?.[field] === "number" && Number.isFinite(data[field]));

const CustomPopper = (props) => (
  <Popper {...props} style={{ width: "fit-content", minWidth: 280 }} placement="bottom-start" />
);

// ─── Stat Card ──
const StatCard = ({ title, value, increase, icon, chartBlue, accent, cardBg, textPrimary, textSecondary, compact, onClick }) => (
  <Paper
    component={onClick ? 'button' : 'div'}
    onClick={onClick}
    elevation={2}
    sx={{
      p: compact ? "1rem" : "1.4rem 1.6rem",
      borderRadius: "16px",
      bgcolor: cardBg,
      boxShadow: "0 4px 18px rgba(0,0,0,0.07)",
      color: textPrimary,
      display: "flex",
      alignItems: "center",
      gap: compact ? 1.5 : 2.5,
      border: `1px solid ${accent || chartBlue}20`, width: '100%',
      transition: 'transform 160ms ease, box-shadow 160ms ease',
      '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${accent || chartBlue}18` } : {},
      '&:focus-visible': { outline: '3px solid #1689ff', outlineOffset: 3 },
      textAlign: 'left',
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    <Box
      sx={{
        bgcolor: `${accent || chartBlue}15`,
        "& .MuiSvgIcon-root": { color: accent || chartBlue },
        borderRadius: "12px",
        p: compact ? 1.2 : 1.8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="subtitle2"
        sx={{ color: accent || chartBlue, fontWeight: 700, mb: 0.3, fontSize: compact ? "0.7rem" : undefined }}
      >
        {title}
      </Typography>
      <Typography
        variant={compact ? "h6" : "h5"}
        fontWeight={700}
        sx={{ color: textPrimary, lineHeight: 1.2, fontVariantNumeric: "tabular-nums", fontSize: compact ? "1.15rem" : "1.65rem", overflowWrap: "anywhere" }}
      >
        {value}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: textSecondary,
          mt: 0.3,
          fontSize: compact ? "0.68rem" : "0.75rem",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {increase}
      </Typography>
    </Box>
    {onClick && <ArrowForwardRounded sx={{ color: accent || chartBlue, ml: "auto", fontSize: 18, flexShrink: 0 }} />}
  </Paper>
);

// ─── Dashboard ──
const Dashboard = () => {
  const navigate = useNavigate();
  const { role } = checkAdminAuth();
  const canViewCustomers = ["admin", "superadmin"].includes(role);
  const theme = useTheme();
  const mode = theme.palette.mode;
  const isDark = mode === "dark";

  // ── Theme-aware colors ──
  const chartBlue    = "#33b0f7";
  const pageBg       = isDark ? theme.palette.background.default : "#f5f9ff";
  const cardBg       = isDark ? theme.palette.background.alt || "#1a1d27" : "#ffffff";
  const textPrimary  = isDark ? "#e8eaf6" : "#001F54";
  const textSecondary = isDark ? "#9e9e9e" : "#546e7a";
  const borderColor  = isDark ? "#2a2d3e" : "#e0e0e0";

  // Responsive breakpoints
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:960px)");

  const currentYear = new Date().getFullYear();

  const [company, setCompany]                     = useState("all");
  const [view, setView]                           = useState("all");
  const [sourceUser, setSourceUser]               = useState("all");
  const [companies, setCompanies]                 = useState([]);
  const [sourceSystems, setSourceSystems]         = useState([]);
  const [customers, setCustomers]                 = useState([]);
  const [customersRequested, setCustomersRequested] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [monthStats, setMonthStats] = useState(null);
  const [monthError, setMonthError] = useState(false);
  const [customerInputValue, setCustomerInputValue] = useState("");
  const [selectedCustomer, setSelectedCustomer]   = useState(null);
  const [stats, setStats]                         = useState(null);
  const [loading, setLoading] = useState(true);
const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError]                         = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [customerError, setCustomerError] = useState("");

  // Chart controls
  const [invoiceViewType, setInvoiceViewType] = useState("yearly");
  const [fromYear, setFromYear]               = useState(2021);
  const [toYear, setToYear]                   = useState(currentYear);
  const [selectedYear, setSelectedYear]       = useState(currentYear);
  const [breakdownView, setBreakdownView]     = useState("invoices");
  const [recordEntity, setRecordEntity] = useState('Customer');
  const [customerInvoiceView, setCustomerInvoiceView] = useState("list");

  // Mobile chart tab

  const isCustomerSelected = view !== "all" && view.trim() !== "";
  const dateRangeError = (!isCustomerSelected || customerInvoiceView === "trend") && invoiceViewType === "yearly" && fromYear > toYear;
  const sourceSystemsForCompany = company === "all"
    ? sourceSystems
    : sourceSystems.filter((system) => String(system.CompanyReferenceID || "").trim() === String(company).trim());
  const years = Array.from({ length: currentYear - 2020 }, (_, i) => 2021 + i);
  const hasActiveFilters = company !== "all" || sourceUser !== "all" || isCustomerSelected;


  const clearDashboardFilters = () => {
    setCompany("all");
    setSourceUser("all");
    setView("all");
    setSelectedCustomer(null);
    setCustomerInputValue("");
    setCustomerError("");
    setInvoiceViewType("yearly");
    setFromYear(2021);
    setToYear(currentYear);
  };

  useEffect(() => {
    setBreakdownView((current) => isCustomerSelected && current === 'customers' ? 'invoices'
      : !isCustomerSelected && !['customers', 'invoices'].includes(current) ? 'customers' : current);
  }, [isCustomerSelected]);

  useEffect(() => {
    const controller = new AbortController();
    const load = async (path, setData) => {
      try {
        const response = await fetch(`${API_BASE}/api/dashboard/${path}`, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!controller.signal.aborted) setData(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err.name !== "AbortError") console.error(err);
      }
    };
    // Each filter becomes ready independently of the customer list.
    load("companies", setCompanies);
    load("source-systems", setSourceSystems);
    return () => controller.abort();
  }, []);

  // Customer search can contain thousands of options. Fetch it only when used,
  // so it doesn't compete with the initial cards and charts.
  useEffect(() => {
    if (!customersRequested) return undefined;
    let active = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);
    setCustomersLoading(true);
    fetch(`${API_BASE}/api/dashboard/customer-load?company=all&sourceUser=all&optionsOnly=true`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => { if (!controller.signal.aborted) setCustomers(Array.isArray(data) ? data : []); })
      .catch(() => {
        if (!active) return;
        setCustomerError("Unable to load customers. Reopen the search to try again.");
        setCustomersRequested(false);
      })
      .finally(() => { clearTimeout(timeout); if (active) setCustomersLoading(false); });
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, [customersRequested]);

  useEffect(() => {
  const controller = new AbortController();
  let monthTimeout;
  const timeout = setTimeout(() => {
    controller.abort();
    setError("Dashboard data is taking too long to load. Please try again.");
    setLoading(false);
    setInitialLoading(false);
  }, 30000);
  const fetchMonthStats = async (dashboardParams) => {
    monthTimeout = setTimeout(() => { setMonthError(true); controller.abort(); }, 30000);
    try {
      const params = new URLSearchParams(dashboardParams);
      params.delete("deferCurrentMonth");
      params.set("currentMonthOnly", "true");
      const response = await fetch(`${API_BASE}/api/dashboard/stats?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!hasMonthStats(data)) throw new Error("Invalid current-month statistics");
      if (!controller.signal.aborted) setMonthStats(data);
    } catch (err) {
      if (!controller.signal.aborted) setMonthError(true);
    } finally {
      clearTimeout(monthTimeout);
    }
  };
  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      setMonthStats(null);
      setMonthError(false);

      const params = new URLSearchParams({ compact: "true", deferCurrentMonth: "true" });
      if (company !== "all") params.set("company", company);
      if (view !== "all") params.set("view", view);
      if (sourceUser !== "all") params.set("sourceUser", sourceUser);
      if (selectedCustomer?.WorkdayCustomerID) params.set("customerId", selectedCustomer.WorkdayCustomerID);
      if (selectedCustomer?.SourceCustomerID) params.set('sourceCustomerId', selectedCustomer.SourceCustomerID);
      if (selectedCustomer?.SourceSystemCode) params.set('customerSourceCode', selectedCustomer.SourceSystemCode);
      if (selectedCustomer?.CustomerMasterLoadID != null) params.set('customerLoadId', selectedCustomer.CustomerMasterLoadID);
      const url = `${API_BASE}/api/dashboard/stats?${params.toString()}`;

      const res = await fetch(url, { signal: controller.signal });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      if (!controller.signal.aborted) {
        setStats(data);
        // Older backends already include these counts. Reuse them instead of
        // downloading the full dashboard a second time during a rolling release.
        if (hasMonthStats(data)) setMonthStats(data);
        else fetchMonthStats(params);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error(err);
      setError("Failed to load dashboard data");
    } finally {
      clearTimeout(timeout);
      if (!controller.signal.aborted) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  };

  fetchDashboard();
  return () => {
    clearTimeout(timeout);
    clearTimeout(monthTimeout);
    controller.abort();
  };
}, [company, sourceUser, view, selectedCustomer?.WorkdayCustomerID, selectedCustomer?.SourceCustomerID, selectedCustomer?.SourceSystemCode, selectedCustomer?.CustomerMasterLoadID, retryCount]);
  

  const filteredCustomers = customers.filter((customer) =>
    (company === "all" || String(customer.CompanyReferenceID || "").trim() === String(company).trim()) &&
    (sourceUser === "all" || customer.SourceSystemCode === sourceUser)
  );

  const searchableCustomers = filteredCustomers.map((customer) => ({
    ...customer,
    searchName: (customer.WorkdayCustomerBusinessEntityName || "").toLowerCase(),
  }));

  useEffect(() => {
    if (!isCustomerSelected || customers.length === 0) return;
    const stillAvailable = filteredCustomers.some((customer) =>
      selectedCustomer?.WorkdayCustomerID
        ? String(customer.WorkdayCustomerID) === String(selectedCustomer.WorkdayCustomerID)
        : customer.WorkdayCustomerBusinessEntityName?.trim().toLowerCase() === view.trim().toLowerCase()
    );
    if (!stillAvailable) {
      setView("all");
      setSelectedCustomer(null);
      setCustomerInputValue("");
      setCustomerError("That customer is not available with the selected company or source system.");
    }
  }, [customers, filteredCustomers, isCustomerSelected, selectedCustomer, view]);

  const handleCustomerConfirm = (value) => {
  if (customersLoading) {
    setCustomerError("Customers are still loading. Please try again in a moment.");
    return;
  }
  if (!value || String(value).trim() === "") {
    setView("all");
    setSelectedCustomer(null);
    setCustomerInputValue("");
    setCustomerError("");
    return;
  }

  let finalValue = typeof value === "object" && value !== null
    ? (value.WorkdayCustomerBusinessEntityName?.trim() || "")
    : String(value).trim();

  if (!finalValue) {
    setView("all");
    setSelectedCustomer(null);
    return;
  }

  // Check if customer actually exists
  const matchingCustomer = filteredCustomers.find(
    (c) => c.WorkdayCustomerBusinessEntityName?.trim().toLowerCase() === finalValue.toLowerCase()
  );

  if (!matchingCustomer) {
    setView("all");
    setSelectedCustomer(null);
    setCustomerError("Choose a customer from the available results.");
    return;
  }

  setCustomerError("");
  setCustomerInputValue(finalValue);
  setSelectedCustomer(typeof value === "object" && value !== null ? value : matchingCustomer);

  // ← ONLY update view here (this triggers fetch)
  if (finalValue !== view) {
    setView(finalValue);
  }
};

  const resetYearRange = useCallback(() => {
    setInvoiceViewType("yearly");
    setFromYear(2021);
    setToYear(currentYear);
  }, [currentYear]);

  // ── Shared select styles ──
  const selectSx = {
    bgcolor: cardBg,
    borderRadius: "10px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    color: textPrimary,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: chartBlue },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: chartBlue },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: chartBlue, borderWidth: 2 },
    "& .MuiSvgIcon-root": { color: chartBlue },
    "& .MuiSelect-select": { color: textPrimary },
  };

  const innerSelectSx = {
    backgroundColor: cardBg,
    borderRadius: "10px",
    color: textPrimary,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: chartBlue, borderWidth: 1.5 },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: chartBlue },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: chartBlue, borderWidth: 2 },
    "& .MuiSelect-select": { color: textPrimary },
  };

  // ── MenuItem dark-mode paper override ──
  const menuPaperSx = isDark
    ? { bgcolor: "#1a1d27", color: textPrimary }
    : {};

  // ── Stat items ──
  const analyticsScope = { company, sourceUser, view, sources: company !== 'all' ? sourceSystemsForCompany.map((system) => system.SourceSystemCode) : undefined, customerId: selectedCustomer?.WorkdayCustomerID,
    sourceCustomerId: selectedCustomer?.SourceCustomerID, customerLoadId: selectedCustomer?.CustomerMasterLoadID,
    customerSourceCode: selectedCustomer?.SourceSystemCode, entity: recordEntity, year: selectedYear };
  const statusParams = new URLSearchParams();
  for (const key of ['company', 'sourceUser', 'view', 'customerId', 'sourceCustomerId', 'customerLoadId', 'customerSourceCode']) {
    if (analyticsScope[key] != null) statusParams.set(key, analyticsScope[key]);
  }
  const statusQuery = statusParams.toString();
  const customerStatus = useRecordStatus(loading ? null : stats, 'Customer', statusQuery);
  const contactStatus = useRecordStatus(loading ? null : stats, 'Contact', statusQuery);
  const invoiceStatus = useRecordStatus(recordEntity === 'Invoice' && !loading ? stats : null, 'Invoice', statusQuery);
  const recordStatus = recordEntity === 'Customer' ? customerStatus
    : recordEntity === 'Contact' ? contactStatus : invoiceStatus;
  const openPage = (kind, extras = {}, period) => navigate(destinationLink(kind, { ...analyticsScope, ...extras }, selectedCustomer, period));
  const chooseSource = (source) => { setSourceUser(source); setCustomerError(''); };
  const customerCount = customerStatus.total ?? summaryCount(customerStatus.data, 'totalCustomers', 'Customer');
  const contactCount = contactStatus.total ?? summaryCount(contactStatus.data, 'totalContacts', 'Contact');
  const exportDashboard = () => downloadCsv(
    ['Company', 'Selected source', 'Customer', 'Metric', 'Value'],
    [['totalCustomers', 'Customers', customerCount], ['totalContacts', 'Contacts', contactCount], ['totalInvoices', 'Invoices', stats?.totalInvoices], ['totalInvoicesAmount', 'Invoice amount', stats?.totalInvoicesAmount]].map(([, label, value]) => [company, sourceUser, view, label, value ?? ''])
      .concat(sourceRows(stats).map((row) => [company, row.id, view, 'Source invoices', row.value]))
      .concat(Object.entries(stats?.statusCounts?.[recordEntity] || {}).map(([status, count]) => [company, sourceUser, view, `${recordEntity} ${status}`, count])),
    'dashboard-summary.csv');
  const selectedCompany = companies.find((item) => item.CompanyReferenceID === company);
  const topSourcesContext = [
    company !== 'all' ? [company, selectedCompany?.WorkdayCustomerBusinessEntityName].filter(Boolean).join(' — ') : null,
    sourceUser !== 'all' ? sourceUser : null,
    selectedCustomer?.WorkdayCustomerBusinessEntityName,
  ].filter(Boolean);
  const topSourcesTitle = isCustomerSelected ? 'Customer Source Systems'
    : sourceUser !== 'all' ? `Source · ${sourceUser}`
      : company !== 'all' ? `Top Sources · ${company}` : 'Top 5 Source Systems';
  const topSourcesSubtitle = topSourcesContext.length
    ? `Invoices · all periods · ${topSourcesContext.join(' · ')}` : 'Invoices · all periods';
  const quickActions = [
    { label: 'View Customers', color: '#1689ff', icon: <Diversity3OutlinedIcon />, disabled: !canViewCustomers, onClick: () => openPage('Customer') },
    { label: 'View Contacts', color: '#00ad73', icon: <CasesOutlinedIcon />, disabled: !canViewCustomers, onClick: () => openPage('Contact') },
    { label: 'View Invoices', color: '#9852ff', icon: <FileCopyOutlinedIcon />, onClick: () => openPage('Invoice') },
    { label: 'View Reports', color: '#f39510', icon: <AssessmentRounded />, onClick: () => openPage('Report') },
  ];

  const statItems = [
    {
      title: "Total Customers",
      onClick: canViewCustomers ? () => openPage('Customer') : undefined,
      accent: '#1689ff',
      value: loading || customerStatus.loading ? 'Loading…' : customerCount == null ? 'Unavailable' : number(customerCount),
      increase: `Current Month: ${monthStats?.currentMonthCustomers ?? (monthError ? "Unavailable" : "Loading…")}`,
      icon: <Diversity3OutlinedIcon sx={{ color: chartBlue, fontSize: isMobile ? "20px" : "24px" }} />,
    },
    {
      title: "Total Contacts",
      onClick: canViewCustomers ? () => openPage('Contact') : undefined,
      accent: '#00ad73',
      value: loading || contactStatus.loading ? 'Loading…' : contactCount == null ? 'Unavailable' : number(contactCount),
      increase: `Current Month: ${monthStats?.currentMonthContacts ?? (monthError ? "Unavailable" : "Loading…")}`,
      icon: <CasesOutlinedIcon sx={{ color: chartBlue, fontSize: isMobile ? "20px" : "24px" }} />,
    },
    {
      title: "Total Invoices",
      onClick: () => openPage('Invoice'),
      accent: '#9852ff',
      value: loading ? 'Loading…' : stats?.totalInvoices == null ? 'Unavailable' : number(stats.totalInvoices),
      increase: `Current Month: ${monthStats?.currentMonthInvoices ?? (monthError ? "Unavailable" : "Loading…")}`,
      icon: <FileCopyOutlinedIcon sx={{ color: chartBlue, fontSize: isMobile ? "20px" : "24px" }} />,
    },
    {
      title: "Invoice Amount",
      onClick: () => openPage("Report"),
      accent: "#f39510",
      value: loading ? "Loading…" : `$${Number(stats?.totalInvoicesAmount ?? 0).toLocaleString()}`,
      increase: monthStats ? `Current Month: $${Number(monthStats.currentMonthInvoiceAmount ?? 0).toLocaleString()}` : `Current Month: ${monthError ? "Unavailable" : "Loading…"}`,
      icon: <AttachMoneyOutlinedIcon sx={{ color: chartBlue, fontSize: isMobile ? "20px" : "24px" }} />,
    },
  ].filter(Boolean);

const invoiceOverviewPanel = (
  <Box
    sx={{
      bgcolor: cardBg,
      p: isMobile ? "1rem" : "1.5rem 2rem",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      color: textPrimary,
    }}
  >
    {/* Header row */}
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1.5,
        gap: 1,
      }}
    >
      <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} color={chartBlue}>
        Invoice Overview
      </Typography>

      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
        {isCustomerSelected && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={customerInvoiceView}
            onChange={(_, value) => value && setCustomerInvoiceView(value)}
            aria-label="Customer invoice view"
            sx={{
              height: 36,
              "& .MuiToggleButton-root": { color: chartBlue, borderColor, textTransform: "none", px: 1.25 },
              "& .Mui-selected": { bgcolor: `${chartBlue} !important`, color: "#fff !important" },
            }}
          >
            <ToggleButton value="list">Invoice list</ToggleButton>
            <ToggleButton value="trend">Trend</ToggleButton>
          </ToggleButtonGroup>
        )}

        {(!isCustomerSelected || customerInvoiceView === "trend") && <FormControl size="small">
          <Select
            value={invoiceViewType}
            onChange={(e) => setInvoiceViewType(e.target.value)}
            sx={{ ...innerSelectSx, minWidth: isMobile ? 100 : 120 }}
            MenuProps={{ PaperProps: { sx: menuPaperSx } }}
          >
            <MenuItem value="yearly">Yearly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
          </Select>
        </FormControl>}

        {(!isCustomerSelected || customerInvoiceView === "trend") && invoiceViewType === "yearly" && (
          <>
            <FormControl size="small">
              <InputLabel sx={{ bgcolor: cardBg, px: "0.4rem", color: chartBlue }}>From</InputLabel>
              <Select
                value={fromYear}
                label="From"
                onChange={(e) => setFromYear(e.target.value)}
                sx={{
                  ...innerSelectSx,
                  minWidth: isMobile ? 80 : 100,
                  ...(dateRangeError && {
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#f44336", borderWidth: 2 },
                  }),
                }}
                MenuProps={{ PaperProps: { sx: menuPaperSx } }}
              >
                {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel sx={{ bgcolor: cardBg, px: "0.4rem", color: chartBlue }}>To</InputLabel>
              <Select
                value={toYear}
                label="To"
                onChange={(e) => setToYear(e.target.value)}
                sx={{
                  ...innerSelectSx,
                  minWidth: isMobile ? 80 : 100,
                  ...(dateRangeError && {
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#f44336", borderWidth: 2 },
                  }),
                }}
                MenuProps={{ PaperProps: { sx: menuPaperSx } }}
              >
                {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </>
        )}

        {(!isCustomerSelected || customerInvoiceView === "trend") && invoiceViewType === "monthly" && (
          <FormControl size="small">
            <InputLabel sx={{ bgcolor: cardBg, px: "0.4rem", color: chartBlue }}>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ ...innerSelectSx, minWidth: isMobile ? 80 : 100 }}
              MenuProps={{ PaperProps: { sx: menuPaperSx } }}
            >
              {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </Box>
    </Box>

    {dateRangeError && (
      <Alert severity="error" sx={{ mb: 1.5, borderRadius: "10px", fontSize: "0.82rem" }}>
        "From" year ({fromYear}) cannot be greater than "To" year ({toYear}).
      </Alert>
    )}

    <Box sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
      {isCustomerSelected && customerInvoiceView === "list" ? (
        <Box p={2}><Typography>Open the invoice page to search and browse invoices for this customer.</Typography><Button onClick={() => { openPage('Invoice'); }}>View invoice records</Button></Box>
      ) : dateRangeError ? (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%" color={textSecondary}>
          Fix the date range above to view the chart.
        </Box>
      ) : invoiceViewType === "yearly" ? (
        <OverviewChart
          data={stats}
          startYear={fromYear}
          endYear={toYear}
          view={view}
          company={company}
          sourceUser={sourceUser}
          isMonthly={false}
          bgColor={cardBg} 
          isDataLoading={loading}
          onResetYearRange={resetYearRange}
        />
      ) : (
        <OverviewChart
          data={stats}
          startYear={selectedYear}
          endYear={selectedYear}
          view={view}
          company={company}
          sourceUser={sourceUser}
          isMonthly={true}
          selectedYear={selectedYear}
          bgColor={cardBg}
          isDataLoading={loading}
          onResetYearRange={resetYearRange}
        />
      )}
    </Box>
  </Box>
);

// ── Breakdown Panel ──
const breakdownPanel = <SourceAnalysis data={stats} isLoading={loading} field={breakdownView === 'customers' ? 'totalCustomers' : breakdownView === 'amount' ? 'totalInvoicesAmount' : 'totalInvoices'}
  title={breakdownView === 'customers' ? 'Customers by Source System' : breakdownView === 'amount' ? 'Amount by Source System' : 'Invoices by Source System'} onSource={chooseSource}
  action={<Select size="small" value={breakdownView} onChange={(event) => setBreakdownView(event.target.value)} inputProps={{ 'aria-label': 'Source chart metric' }} sx={{ fontSize: '0.72rem' }}>
    {!isCustomerSelected && <MenuItem value="customers">Customers</MenuItem>}<MenuItem value="invoices">Total Invoices</MenuItem><MenuItem value="amount">Amount</MenuItem>
  </Select>} />;

  return (
    <Box
      sx={{
        backgroundColor: pageBg,
        minHeight: "100vh",
        width: "100%",
        boxSizing: "border-box",
        p: isMobile
          ? "0.75rem"
          : isTablet
          ? "1rem 1.25rem"
          : "1.5rem 2.5rem",
      }}
    >
      {/* Page Header */}
      <Box sx={{ mb: isMobile ? "1.25rem" : "2rem" }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Header title="DASHBOARD" subtitle="Overview of customers, contacts, invoices and key insights." />
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            {stats?.generatedAt && <Typography sx={{ fontSize: '0.72rem', color: textSecondary, mr: 1 }}>Updated {new Date(stats.generatedAt).toLocaleString()}</Typography>}
            <Button color="info" variant="outlined" startIcon={<DownloadRounded />} disabled={!stats || loading || Boolean(error)} onClick={exportDashboard}>Export Dashboard</Button>
            <Button color="info" variant="outlined" startIcon={<RefreshRounded />} disabled={loading} onClick={() => setRetryCount((count) => count + 1)}>Refresh</Button>
          </Box>
        </Box>
      </Box>

      {/* Error */}
      {error && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: "12px", bgcolor: cardBg, color: textPrimary }}>
          <Alert severity="error" action={<Button color="inherit" onClick={() => setRetryCount((count) => count + 1)}>Retry</Button>}>{error}</Alert>
        </Paper>
      )}
      {monthError && !error && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => setRetryCount((count) => count + 1)}>Retry</Button>}>
          Current-month counts could not be loaded.
        </Alert>
      )}

      {/* ── Filters ── */}
      <Box
        sx={{
          mb: isMobile ? "1.25rem" : "2rem",
          display: "flex",
          flexWrap: "wrap",
          gap: isMobile ? 1.5 : 2,
          alignItems: "flex-end",
          p: 2, bgcolor: cardBg, borderRadius: "16px", border: `1px solid ${borderColor}`,
        }}
      >
        {/* Company */}
        <FormControl sx={{ minWidth: isMobile ? "calc(50% - 8px)" : 160, flex: isMobile ? "1 1 auto" : "0 0 auto" }}>
          <InputLabel
  sx={{
    color: chartBlue,
    fontWeight: 500,
    "&.Mui-focused": {
      color: chartBlue,
    },
  }}
>
  Company
</InputLabel>
          <Select
            value={company}
            label="Company"
            onChange={(e) => {
              setCompany(e.target.value);
              setCustomerError("");
              if (e.target.value !== "all") setSourceUser("all");
            }}
            sx={selectSx}
            MenuProps={{ PaperProps: { sx: menuPaperSx } }}
          >
            <MenuItem value="all">All</MenuItem>
            {companies.map((c) => (
              <MenuItem key={c.CompanyReferenceID} value={c.CompanyReferenceID}>
                {c.WorkdayCustomerBusinessEntityName
                  ? `${c.CompanyReferenceID} — ${c.WorkdayCustomerBusinessEntityName}`
                  : c.CompanyReferenceID}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Source System */}
        <FormControl sx={{ minWidth: isMobile ? "calc(50% - 8px)" : 160, flex: isMobile ? "1 1 auto" : "0 0 auto" }}>
          <InputLabel
  sx={{
    color: chartBlue,
    fontWeight: 500,
    "&.Mui-focused": {
      color: chartBlue,
    },
  }}
>
Source System</InputLabel>
          <Select
            value={sourceUser}
            label="Source System"
            onChange={(e) => {
              setSourceUser(e.target.value);
              setCustomerError("");
            }}
            sx={selectSx}
            MenuProps={{ PaperProps: { sx: menuPaperSx } }}
          >
            <MenuItem value="all">All</MenuItem>
            {sourceSystemsForCompany.map((s) => (
              <MenuItem key={s.SourceSystemCode} value={s.SourceSystemCode}>
                {s.SourceSystemName ? `${s.SourceSystemCode} — ${s.SourceSystemName}` : s.SourceSystemCode}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Customer Search */}
        <Autocomplete
          onOpen={() => { setCustomerError(""); setCustomersRequested(true); }}
          loading={customersLoading}
          freeSolo
          disableClearable
          options={searchableCustomers}
          ListboxComponent={CustomerListbox}
          ListboxProps={{ filterKey: `${company}|${sourceUser}|${customerInputValue}` }}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option?.WorkdayCustomerBusinessEntityName || ""
          }
          isOptionEqualToValue={(option, value) =>
            String(option?._id || option?.WorkdayCustomerID || "") === String(value?._id || value?.WorkdayCustomerID || "") &&
            option?.SourceSystemCode === value?.SourceSystemCode
          }
          renderOption={(props, option) => [
            { ...props, key: `${option.SourceSystemCode}-${option._id || option.WorkdayCustomerID}` }, option, isDark,
          ]}
          value={selectedCustomer}
          inputValue={customerInputValue}
          onInputChange={(_, newVal) => {
  setCustomerInputValue(newVal);
  setCustomerError("");

  // Only reset when user clears the field
  if (!newVal?.trim()) {
    setView("all");
    setSelectedCustomer(null);
  }
  // DO NOT call handleCustomerConfirm here
}}
          onChange={(_, newVal) => handleCustomerConfirm(newVal)}
          onKeyDown={(e) => {
  prepareCustomerNavigation(e);
  if (e.key === "Enter") {
    handleCustomerConfirm(customerInputValue);
    e.preventDefault();
  }
}}
          filterOptions={(options, { inputValue }) => {
            if (!inputValue || inputValue.length < 1) return options;
            const lower = inputValue.toLowerCase().trim();
            return options.filter((opt) =>
              opt.searchName.includes(lower)
            );
          }}

          PopperComponent={CustomPopper}
          sx={{ flex: isMobile ? "1 1 100%" : "1 1 220px", maxWidth: isMobile ? "100%" : 360 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Customer"
              placeholder="Type customer name..."
              error={Boolean(customerError)}
              helperText={customerError}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: cardBg,
                  borderRadius: "10px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  color: textPrimary,
                  "& fieldset": { borderColor: chartBlue },
                  "&:hover fieldset": { borderColor: chartBlue },
                  "&.Mui-focused fieldset": { borderColor: chartBlue, borderWidth: 2 },
                },
                "& .MuiInputLabel-root": {
  color: chartBlue,
  "&.Mui-focused": {
    color: chartBlue,
  },
},
                "& .MuiInputBase-input": {
                  color: isDark ? "#f1f5f9" : "#111827",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  "&::placeholder": { color: isDark ? "#cbd5e1" : "#475569", opacity: 1 },
                },
              }}
            />
          )}
        />
        {hasActiveFilters && (
          <Button
            onClick={clearDashboardFilters}
            variant="outlined"
            sx={{
              minHeight: 40,
              borderRadius: "10px",
              borderColor: chartBlue,
              color: chartBlue,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            Clear filters
          </Button>
        )}
      </Box>

      {/* ── Loading ── */}
{initialLoading ? (
  <PageLoader variant="region" minHeight="60vh" />
) : error ? null : (
        <>
          {/* ── Stat Cards ── */}
          <Box
            display="grid"
            gridTemplateColumns={
              isMobile
                ? "repeat(2, 1fr)"
                : isTablet
                ? "repeat(2, 1fr)"
                : "repeat(auto-fit, minmax(220px, 1fr))"
            }
            gap={isMobile ? 1.5 : 2.5}
            mb={isMobile ? "1.5rem" : "2.5rem"}
          >
            {statItems.map((item, i) => (
              <StatCard
                key={i}
                {...item}
                chartBlue={chartBlue}
                cardBg={cardBg}
                textPrimary={textPrimary}
                textSecondary={textSecondary}
                compact={isMobile}
              />
            ))}
          </Box>

          {/* ── Charts ── */}

          {/* MOBILE: Tabbed charts */}
          {stats?.missingInvoiceReferenceLines > 0 && <Alert severity="warning" sx={{ mb: 2 }}>{stats.missingInvoiceReferenceLines} invoice lines have missing or blank references and are reported separately from invoice totals.</Alert>}
          {Object.entries(stats?.missingDateCounts || {}).some(([, count]) => count > 0) && <Alert severity="info" sx={{ mb: 2 }}>Monthly counts include dated records only. Missing dates: {Object.entries(stats.missingDateCounts).filter(([, count]) => count > 0).map(([entity, count]) => `${entity}: ${count}`).join('; ')}.</Alert>}
          <StatusCards data={loading ? null : recordStatus.data} entity={recordEntity} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'minmax(0, 1.8fr) minmax(0, 1.1fr) minmax(240px, .8fr)' }, gridAutoRows: { xl: 640 }, gap: 2, alignItems: 'stretch', mb: 2 }}>
            <Paper elevation={0} sx={{ gridColumn: { md: '1 / -1', xl: 'auto' }, height: { xs: 450, sm: 500, md: 540, xl: '100%' }, minWidth: 0, borderRadius: '16px', overflow: 'hidden', border: `1px solid ${borderColor}` }}>{invoiceOverviewPanel}</Paper>
            {breakdownPanel}
            <RecordStatus data={recordStatus.data} entity={recordEntity} loading={loading || recordStatus.loading} error={recordStatus.error} onRetry={recordStatus.retry} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
            <TopSources data={loading ? null : stats} onSource={chooseSource} title={topSourcesTitle} subtitle={topSourcesSubtitle} />
            <MonthlyTrend data={loading ? null : stats} year={selectedYear} onPeriod={(month) => openPage('Invoice', {}, { fromDate: `${selectedYear}-${String(month).padStart(2, '0')}-01`, toDate: new Date(Number(selectedYear), month, 0).toLocaleDateString('fr-CA') })} />
            <QuickActions actions={quickActions} />
            <Insights data={loading ? null : stats} entity={recordEntity} year={selectedYear} />
          </Box>
          <EntityRecords company={company} sourceUser={sourceUser} view={view} customerId={selectedCustomer?.WorkdayCustomerID} customerLoadId={selectedCustomer?.CustomerMasterLoadID} sourceCustomerId={selectedCustomer?.SourceCustomerID} customerSourceCode={selectedCustomer?.SourceSystemCode} entity={recordEntity} onEntityChange={setRecordEntity} />
        </>
      )}

      {/* ── AI Chat FAB ── */}
      <Box
        sx={{
          position: "fixed",
          bottom: isMobile ? "12px" : "20px",
          right: isMobile ? "12px" : "20px",
          zIndex: 9999,
        }}
      >
        <AIChat />
      </Box>
    </Box>
  );
};

export default Dashboard;
